/**
 * Hariom ERP - Inventory Management Page
 * Track stock levels for all products. Data is entered by employees (Add
 * Product), editable, deletable, persisted, and exportable to Excel.
 */
import React, { useState } from 'react'
import { Package, Plus, AlertTriangle, Search, Download, Edit3, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useInventoryStore, type InvProduct } from '../../store/inventoryStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'
import { exportRowsToExcel } from '../../utils/exportExcel'

// Dropdown options for the Add/Edit form.
const CATEGORIES = ['Epoxy TMT', 'Epoxy Dowel', 'Fusion TMT', 'Fusion Dowel', 'MS Dowel', 'Binding Wire', 'Raw Material']
const UNITS = ['Ton', 'Kg', 'Piece', 'Meter']

// Fields rendered in the product pop-up form.
const PRODUCT_FIELDS: FormField[] = [
  { name: 'code',     label: 'Product Code', required: true, placeholder: 'e.g. HE-TMT-12' },
  { name: 'name',     label: 'Product Name', required: true, placeholder: 'e.g. Epoxy TMT 12mm', full: true },
  { name: 'category', label: 'Category', type: 'select', options: CATEGORIES, required: true },
  { name: 'unit',     label: 'Unit', type: 'select', options: UNITS },
  { name: 'stock',    label: 'Current Stock', type: 'number', placeholder: '0' },
  { name: 'price',    label: 'Unit Price (₹)', type: 'number', placeholder: '0' },
  { name: 'min',      label: 'Min Stock', type: 'number', placeholder: '0' },
  { name: 'max',      label: 'Max Stock', type: 'number', placeholder: '0' },
  { name: 'location', label: 'Location', placeholder: 'e.g. W-A01' },
]

const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

const Inventory: React.FC = () => {
  const { items: products, add, update, remove } = useInventoryStore()
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<InvProduct | null>(null)

  const lowStockItems = products.filter(p => p.stock <= p.min && p.max > 0)
  const filtered = products.filter(p => {
    const ms = p.name.toLowerCase().includes(search.toLowerCase()) || p.code.toLowerCase().includes(search.toLowerCase())
    const mc = catFilter === 'all' || p.category === catFilter
    return ms && mc
  })
  const categories = [...new Set(products.map(p => p.category))]

  const getStockStatus = (p: InvProduct) => {
    if (p.max > 0 && p.stock <= p.min) return { label: 'Low Stock', cls: 'badge-danger' }
    const pct = p.max > 0 ? (p.stock / p.max) * 100 : 100
    if (pct < 40) return { label: 'Medium', cls: 'badge-warning' }
    return { label: 'Good', cls: 'badge-success' }
  }

  // Save handler shared by Add + Edit.
  const handleSave = (v: Record<string, string>) => {
    // Block duplicate product codes (except the one being edited).
    const code = v.code.trim()
    if (products.some(p => p.code.toLowerCase() === code.toLowerCase() && p.id !== editing?.id)) {
      toast.error(`Code "${code}" already exists`)
      return
    }
    const record: InvProduct = {
      id: editing ? editing.id : `prod-${Date.now()}`,
      code,
      name: v.name.trim(),
      category: v.category || 'Raw Material',
      stock: num(v.stock), min: num(v.min), max: num(v.max),
      unit: v.unit || 'Ton',
      price: num(v.price),
      location: v.location.trim(),
    }
    if (editing) { update(editing.id, record); toast.success(`Updated ${record.name}`) }
    else { add(record); toast.success(`Added ${record.name}`) }
    setModalOpen(false); setEditing(null)
  }

  const handleDelete = (p: InvProduct) => {
    if (window.confirm(`Delete "${p.name}"? This cannot be undone.`)) {
      remove(p.id); toast.success(`Deleted ${p.name}`)
    }
  }

  const handleExport = () => {
    if (!products.length) { toast.error('No products to export'); return }
    exportRowsToExcel(
      products.map(p => ({
        Code: p.code, 'Product Name': p.name, Category: p.category,
        'Current Stock': p.stock, Unit: p.unit, 'Min': p.min, 'Max': p.max,
        'Unit Price': p.price, Location: p.location,
        Status: getStockStatus(p).label,
      })),
      'Inventory', 'Hariom_Inventory'
    )
    toast.success('Inventory exported to Excel')
  }

  const toRecord = (p: InvProduct): Record<string, string> => ({
    code: p.code, name: p.name, category: p.category, unit: p.unit,
    stock: String(p.stock), price: String(p.price), min: String(p.min),
    max: String(p.max), location: p.location,
  })

  const stockValueCr = (products.reduce((s, p) => s + p.stock * p.price, 0) / 10000000).toFixed(2)
  const finishedGoods = products.filter(p => p.category !== 'Raw Material').reduce((s, p) => s + p.stock, 0)

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Inventory Management</h1>
          <p className="page-subtitle">Real-time stock levels for all Hariom products &amp; raw materials</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} className="btn-secondary text-sm flex items-center gap-2"><Download size={14}/> Export</button>
          <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> Add Product</button>
        </div>
      </div>

      {/* Low stock alerts */}
      {lowStockItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-red-600" />
            <h3 className="font-semibold text-red-800 text-sm">Low Stock Alert — {lowStockItems.length} items below minimum</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.map(item => (
              <span key={item.id} className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium">
                {item.name}: {item.stock} {item.unit} (min: {item.min})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500">
          <p className="text-xs text-steel-500">Total SKUs</p>
          <p className="text-2xl font-bold text-steel-900">{products.length}</p>
        </div>
        <div className="card py-3 border-l-4 border-l-red-500">
          <p className="text-xs text-steel-500">Low Stock Items</p>
          <p className="text-2xl font-bold text-red-600">{lowStockItems.length}</p>
        </div>
        <div className="card py-3 border-l-4 border-l-green-500">
          <p className="text-xs text-steel-500">Total Stock Value</p>
          <p className="text-2xl font-bold text-steel-900">₹{stockValueCr} Cr</p>
        </div>
        <div className="card py-3 border-l-4 border-l-purple-500">
          <p className="text-xs text-steel-500">Finished Goods (Ton)</p>
          <p className="text-2xl font-bold text-steel-900">{finishedGoods}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 bg-white border border-steel-200 rounded-lg px-3 py-2 flex-1 min-w-48 max-w-xs">
          <Search size={14} className="text-steel-400" />
          <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-sm outline-none flex-1" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="form-input w-auto text-sm">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Products Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-steel-50 border-b border-steel-200">
                <th className="table-header">Code</th>
                <th className="table-header">Product Name</th>
                <th className="table-header">Category</th>
                <th className="table-header text-right">Current Stock</th>
                <th className="table-header text-right">Min / Max</th>
                <th className="table-header text-center">Status</th>
                <th className="table-header text-right">Unit Price (₹)</th>
                <th className="table-header text-center">Location</th>
                <th className="table-header text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-steel-400 text-sm py-10">
                    No products yet. Click <span className="font-semibold text-steel-600">“Add Product”</span> to add your first item.
                  </td>
                </tr>
              ) : filtered.map(p => {
                const status = getStockStatus(p)
                const pct = p.max > 0 ? Math.min(100, Math.round((p.stock / p.max) * 100)) : 0
                return (
                  <tr key={p.id} className={clsx('table-row-hover', p.max > 0 && p.stock <= p.min && 'bg-red-50/50')}>
                    <td className="table-cell font-mono text-xs text-steel-500">{p.code}</td>
                    <td className="table-cell font-medium text-steel-800">{p.name}</td>
                    <td className="table-cell">
                      <span className="text-xs bg-steel-100 text-steel-600 px-2 py-0.5 rounded">{p.category}</span>
                    </td>
                    <td className="table-cell text-right">
                      <div>
                        <p className="font-bold text-steel-800">{p.stock} {p.unit}</p>
                        <div className="w-24 bg-steel-100 rounded-full h-1 mt-1 ml-auto">
                          <div className={clsx('h-1 rounded-full', pct < 30 ? 'bg-red-500' : pct < 60 ? 'bg-yellow-500' : 'bg-green-500')}
                            style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="table-cell text-right text-xs text-steel-500">{p.min} / {p.max}</td>
                    <td className="table-cell text-center"><span className={status.cls}>{status.label}</span></td>
                    <td className="table-cell text-right text-steel-700">₹{p.price.toLocaleString()}</td>
                    <td className="table-cell text-center font-mono text-xs">{p.location || '—'}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setEditing(p); setModalOpen(true) }}
                          className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit">
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => handleDelete(p)}
                          className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit modal */}
      {modalOpen && (
        <FormModal
          title={editing ? 'Edit Product' : 'Add Product'}
          submitLabel={editing ? 'Save Changes' : 'Add Product'}
          fields={PRODUCT_FIELDS}
          initial={editing ? toRecord(editing) : {}}
          onClose={() => { setModalOpen(false); setEditing(null) }}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default Inventory
